import React, { useState, useEffect } from "react";
import {
  Building2,
  Users,
  HardDrive,
  Mail,
  AlertTriangle,
  ShieldCheck,
  ArrowRight,
  RefreshCw,
  Database,
  Activity,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { api } from "../../../lib/api";
function formatLogTime(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return ts;
  }
}

export default function OverviewTab({ onSelectTab }) {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [health, setHealth] = useState(null);
  const [smtp, setSmtp] = useState(null);
  const [recentErrors, setRecentErrors] = useState([]);
  const [recentAudits, setRecentAudits] = useState([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [compRes, healthRes, smtpRes, errRes, auditRes] = await Promise.allSettled([
        api.get("/super-admin/companies"),
        api.get("/super-admin/system/health"),
        api.get("/super-admin/smtp"),
        api.get("/super-admin/logs/errors?limit=5"),
        api.get("/super-admin/logs/audit?limit=5"),
      ]);

      if (compRes.status === "fulfilled") setCompanies(compRes.value.data || []);
      if (healthRes.status === "fulfilled") setHealth(healthRes.value.data || null);
      if (smtpRes.status === "fulfilled") setSmtp(smtpRes.value.data || null);
      if (errRes.status === "fulfilled") setRecentErrors(errRes.value.data || []);
      if (auditRes.status === "fulfilled") setRecentAudits(auditRes.value.data || []);
    } catch (err) {
      toast.error("Failed to load some overview telemetry");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const totalCompanies = companies.length;
  const activeCompanies = companies.filter((c) => c.status === "active").length;
  const suspendedCompanies = companies.filter((c) => c.status === "suspended").length;
  const totalProperties = companies.reduce((acc, c) => acc + (c.properties_count || 0), 0);
  const totalUsers = companies.reduce((acc, c) => acc + (c.users_count || 0), 0);
  const totalDbBytes = companies.reduce((acc, c) => acc + (c.db_size_bytes || 0), 0);
  const totalDbMb = (totalDbBytes / (1024 * 1024)).toFixed(2);

  return (
    <div className="space-y-6">
      {/* Top Banner & Refresh */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5 rounded-2xl bg-gradient-to-r from-slate-900 via-slate-900/90 to-teal-950/40 border border-slate-800 shadow-xl">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">
              System Operational
            </span>
            {health?.maintenance_mode && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold tracking-wider uppercase bg-amber-500/20 text-amber-300 border border-amber-500/40 ml-2">
                Maintenance Mode Active
              </span>
            )}
          </div>
          <h2 className="text-xl font-bold text-white tracking-tight">Platform Telemetry Overview</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Real-time aggregate status across all isolated company instances, mail servers, and server disks.
          </p>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-center">
          <button
            onClick={loadData}
            disabled={loading}
            className="px-3.5 py-2 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/70 text-slate-200 text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-teal-400" : ""}`} />
            <span>Refresh Telemetry</span>
          </button>
        </div>
      </div>

      {/* Metric Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3.5">
        {/* Total Companies */}
        <div
          onClick={() => onSelectTab("companies")}
          className="group cursor-pointer p-4 rounded-xl bg-slate-900/70 hover:bg-slate-800/70 border border-slate-800 hover:border-teal-500/40 transition-all shadow-lg flex flex-col justify-between"
        >
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider">Companies</span>
            <Building2 className="w-4 h-4 text-teal-400" />
          </div>
          <div>
            <div className="text-2xl font-black text-white tracking-tight">{totalCompanies}</div>
            <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1.5">
              <span className="text-emerald-400 font-semibold">{activeCompanies} active</span>
              {suspendedCompanies > 0 && (
                <span className="text-rose-400 font-semibold">({suspendedCompanies} suspended)</span>
              )}
            </div>
          </div>
        </div>

        {/* Properties */}
        <div className="p-4 rounded-xl bg-slate-900/70 border border-slate-800 shadow-lg flex flex-col justify-between">
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider">Properties</span>
            <Activity className="w-4 h-4 text-teal-400" />
          </div>
          <div>
            <div className="text-2xl font-black text-white tracking-tight">{totalProperties}</div>
            <div className="text-[11px] text-slate-400 mt-1">Managed assets across tenants</div>
          </div>
        </div>

        {/* Total Users */}
        <div className="p-4 rounded-xl bg-slate-900/70 border border-slate-800 shadow-lg flex flex-col justify-between">
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider">Tenant Users</span>
            <Users className="w-4 h-4 text-blue-400" />
          </div>
          <div>
            <div className="text-2xl font-black text-white tracking-tight">{totalUsers}</div>
            <div className="text-[11px] text-slate-400 mt-1">Owners, editors, and viewers</div>
          </div>
        </div>

        {/* Database Storage */}
        <div
          onClick={() => onSelectTab("health")}
          className="group cursor-pointer p-4 rounded-xl bg-slate-900/70 hover:bg-slate-800/70 border border-slate-800 hover:border-teal-500/40 transition-all shadow-lg flex flex-col justify-between"
        >
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider">DB Storage</span>
            <Database className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <div className="text-2xl font-black text-white tracking-tight">{totalDbMb} MB</div>
            <div className="text-[11px] text-slate-400 mt-1">Total SQLite databases</div>
          </div>
        </div>

        {/* Global SMTP */}
        <div
          onClick={() => onSelectTab("smtp")}
          className="group cursor-pointer p-4 rounded-xl bg-slate-900/70 hover:bg-slate-800/70 border border-slate-800 hover:border-teal-500/40 transition-all shadow-lg flex flex-col justify-between"
        >
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider">Global SMTP</span>
            <Mail className="w-4 h-4 text-indigo-400" />
          </div>
          <div>
            <div className="text-sm font-bold text-white tracking-tight mt-1">
              {smtp?.is_configured ? (
                <span className="text-emerald-400 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" /> Ready
                </span>
              ) : (
                <span className="text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> Unconfigured
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-400 mt-1 truncate">
              {smtp?.smtp_host ? `${smtp.smtp_host}:${smtp.smtp_port}` : "Tenant fallback pending"}
            </div>
          </div>
        </div>

        {/* Disk Used */}
        <div
          onClick={() => onSelectTab("health")}
          className="group cursor-pointer p-4 rounded-xl bg-slate-900/70 hover:bg-slate-800/70 border border-slate-800 hover:border-teal-500/40 transition-all shadow-lg flex flex-col justify-between"
        >
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider">Host Disk</span>
            <HardDrive className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <div className="text-2xl font-black text-white tracking-tight">
              {health?.disk?.used_percent != null ? `${health.disk.used_percent}%` : "—"}
            </div>
            <div className="text-[11px] text-slate-400 mt-1">
              {health?.disk?.free_gb != null ? `${health.disk.free_gb} GB free` : "Checking..."}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Launchpad Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <button
          onClick={() => onSelectTab("companies")}
          className="p-3.5 rounded-xl bg-slate-900/60 hover:bg-slate-800/80 border border-slate-800 hover:border-slate-700 text-left transition-all flex items-center justify-between group"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-teal-500/10 text-teal-400 flex items-center justify-center">
              <Building2 className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs font-bold text-white group-hover:text-teal-400 transition-colors">
                Inspect Companies
              </div>
              <div className="text-[11px] text-slate-400">View tenants & Jump In</div>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-teal-400 transition-colors" />
        </button>

        <button
          onClick={() => onSelectTab("smtp")}
          className="p-3.5 rounded-xl bg-slate-900/60 hover:bg-slate-800/80 border border-slate-800 hover:border-slate-700 text-left transition-all flex items-center justify-between group"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/10 text-indigo-400 flex items-center justify-center">
              <Mail className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs font-bold text-white group-hover:text-indigo-400 transition-colors">
                Configure SMTP
              </div>
              <div className="text-[11px] text-slate-400">Mail server & test delivery</div>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-indigo-400 transition-colors" />
        </button>

        <button
          onClick={() => onSelectTab("health")}
          className="p-3.5 rounded-xl bg-slate-900/60 hover:bg-slate-800/80 border border-slate-800 hover:border-slate-700 text-left transition-all flex items-center justify-between group"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs font-bold text-white group-hover:text-emerald-400 transition-colors">
                DB Integrity Checks
              </div>
              <div className="text-[11px] text-slate-400">Validate SQLite storage</div>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 transition-colors" />
        </button>

        <button
          onClick={() => onSelectTab("settings")}
          className="p-3.5 rounded-xl bg-slate-900/60 hover:bg-slate-800/80 border border-slate-800 hover:border-slate-700 text-left transition-all flex items-center justify-between group"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center">
              <HardDrive className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs font-bold text-white group-hover:text-amber-400 transition-colors">
                Disaster Recovery
              </div>
              <div className="text-[11px] text-slate-400">1-Click system snapshot</div>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-amber-400 transition-colors" />
        </button>
      </div>

      {/* Two Columns: Recent Errors & Recent Audits */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Errors */}
        <div className="p-5 rounded-2xl bg-slate-900/70 border border-slate-800 shadow-xl flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-400" />
                <h3 className="text-sm font-bold text-white tracking-tight">Recent System Errors</h3>
              </div>
              <button
                onClick={() => onSelectTab("errors")}
                className="text-xs text-teal-400 hover:underline flex items-center gap-1 font-semibold"
              >
                <span>View All</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {recentErrors.length === 0 ? (
              <div className="py-8 text-center text-xs text-slate-500 flex flex-col items-center gap-2">
                <CheckCircle className="w-6 h-6 text-emerald-500/50" />
                <span>Zero logged system errors. Clean operations!</span>
              </div>
            ) : (
              <div className="divide-y divide-slate-800/80 text-xs">
                {recentErrors.map((err) => (
                  <div key={err.id} className="py-2.5 space-y-1">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-400 font-mono font-bold">
                        {err.level}
                      </span>
                      <span className="text-slate-500">{formatLogTime(err.timestamp || err.created_at)}</span>
                    </div>
                    <div className="text-slate-200 font-medium truncate">{err.message}</div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {err.source} • {err.endpoint || "no endpoint"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Security Audits */}
        <div className="p-5 rounded-2xl bg-slate-900/70 border border-slate-800 shadow-xl flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-teal-400" />
                <h3 className="text-sm font-bold text-white tracking-tight">Security Audit Activity</h3>
              </div>
              <button
                onClick={() => onSelectTab("audit")}
                className="text-xs text-teal-400 hover:underline flex items-center gap-1 font-semibold"
              >
                <span>View All</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {recentAudits.length === 0 ? (
              <div className="py-8 text-center text-xs text-slate-500">
                No security events logged yet.
              </div>
            ) : (
              <div className="divide-y divide-slate-800/80 text-xs">
                {recentAudits.map((a) => (
                  <div key={a.id} className="py-2.5 space-y-1">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="px-1.5 py-0.5 rounded bg-teal-500/10 text-teal-300 font-mono font-bold">
                        {a.event_type}
                      </span>
                      <span className="text-slate-500">{formatLogTime(a.timestamp || a.created_at)}</span>
                    </div>
                    <div className="text-slate-200 truncate">{a.details || "No details provided"}</div>
                    <div className="text-[10px] text-slate-500 truncate">
                      Actor: {a.actor || "system"} • IP: {a.client_ip || "unknown"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
